// Shared capacity-based inventory math for Tier II.
// Used by both the questionnaire's save-time calculation and Threshold Review so the
// number a user previews always matches the value that gets persisted.
import { usageAmountToPounds } from '../../tri-shared/services/formulas/units.ts';

/** One row in a multi-vessel capacity-based inventory entry. */
export interface CapacityLine {
  id: string;
  description: string;
  capacityAmount: number | null;
  capacityUnit: string;
  maxCount: number | null;
  avgCount: number | null;
  avgMode?: 'count' | 'percent';
  avgPercent?: number | null;
  /**
   * The physical vessel chosen during setup on the Data Entry page (a catalog id such as
   * `steel-drum`). It drives the artwork and preset capacities, and determines `storageType`.
   * Absent on lines saved before the vessel catalog existed.
   */
  vesselType?: string | null;
  /** Custom type label when vesselType is other. */
  vesselTypeName?: string | null;
  /**
   * Per-vessel storage attributes for the Tier II report. Codes match the report form's
   * option lists (storageType A–R, pressure/temperature A–E).
   *
   * `storageType` follows from the chosen `vesselType`, so the report inherits a storage type
   * without asking again. Pressure and temperature are still only prompted on the Form R report
   * page, and only when the product is reportable.
   */
  storageType?: string | null;
  pressure?: string | null;
  temperature?: string | null;
}

/** Pounds on hand for a vessel count. Null means unconvertible, not zero inventory. */
export function capacityLineToLbs(
  line: CapacityLine,
  count: number | null,
  specificGravity: number | null,
): number | null {
  if (line.capacityAmount == null || count == null) return null;
  const result = usageAmountToPounds(line.capacityAmount * count, line.capacityUnit, specificGravity);
  return typeof result === 'number' ? result : null;
}

/**
 * Max and average pounds on hand across all vessel lines.
 * `avgAmountLbs` is null when no line provides average data (count or percent).
 * Either total is also null if any contributing line cannot be converted.
 */
export function capacityLinesToLbs(
  lines: CapacityLine[],
  specificGravity: number | null,
): { maxAmountLbs: number | null; avgAmountLbs: number | null } {
  // Never sum only the convertible rows: a partial total would understate inventory.
  const sumKnown = (values: (number | null)[]): number | null =>
    values.reduce<number | null>((sum, value) => (sum == null || value == null ? null : sum + value), 0);
  const maxAmountLbs = sumKnown(lines.map((line) => capacityLineToLbs(line, line.maxCount, specificGravity)));
  const hasAvg = lines.some((l) => l.avgCount != null || l.avgMode === 'percent');
  const avgAmountLbs = hasAvg
    ? sumKnown(
        lines.map((line) => {
          if (line.avgMode === 'percent') {
            if (line.avgPercent == null) return null;
            const max = capacityLineToLbs(line, line.maxCount, specificGravity);
            return max == null ? null : max * (line.avgPercent / 100);
          }
          return capacityLineToLbs(line, line.avgCount, specificGravity);
        }),
      )
    : null;
  return { maxAmountLbs, avgAmountLbs };
}
